/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Location } from '@touchplan/common';
import { Role } from '@touchplan/common';
import { ProjectMember } from '@touchplan/common';
import { Plan } from '@touchplan/common';
import { Project } from '../app-data/models/Project';
import { InvitedUser } from '../app-data/models/InvitedUser';
import { CustomFieldDefinition } from '../app-data/models/CustomFieldDefinition';
import { ICustomFieldDropDownItemDefinition } from '../app-data/models/CustomFieldDropDownDefinition';
import { Ticket, TicketFields } from '@touchplan/common';

export class ArrayUtils {}

function anyNullOrUndefined<T>(...a: T[]) {
	return a.some((thing) => thing === null || thing === undefined);
}

/**
 * Can be used as an addition to a sort function for handling the null cases when the auto-handling is insufficient `.localeCompare` for
 * example. Currently puts null values as lower than defined values.
 */
export function nullComp<T>(a: T, b: T) {
	const aNull = a === null || a === undefined;
	const bNull = b === null || b === undefined;
	if (aNull && bNull) {
		return 0;
	} else if (aNull) {
		return 1;
	} else {
		return -1;
	}
}

export function sortProjectsByName(a: Project, b: Project): number {
	if (anyNullOrUndefined(a.view.projectName, b.view.projectName)) {
		return nullComp(a.view.projectName, b.view.projectName);
	}
	return a.view.projectName.localeCompare(b.view.projectName);
}

export function sortPlansByCreationDate(a: Plan, b: Plan): number {
	return new Date(b.view.createdAt).getTime() - new Date(a.view.createdAt).getTime();
}

export function sortPlansByName(a: Plan, b: Plan): number {
	if (anyNullOrUndefined(a.view.name, b.view.name)) {
		return nullComp(a.view.name, b.view.name);
	}
	return a.view.name.localeCompare(b.view.name);
}

export function sortTicketsByCreationDate(a: Ticket, b: Ticket): number {
	return sortTicketFieldsByStartDate(a.view, b.view);
}

export function sortTicketsByEndDate(a: Ticket, b: Ticket): number {
	return sortTicketFieldsByEndDate(a.view, b.view);
}

export function sortTicketFieldsByStartDate(a: TicketFields, b: TicketFields): number {
	const aTicketDateStart = getTicketFieldsStartDate(a);
	const bTicketDateStart = getTicketFieldsStartDate(b);
	return new Date(aTicketDateStart).getTime() - new Date(bTicketDateStart).getTime();
}

export function sortTicketFieldsByEndDate(a: TicketFields, b: TicketFields): number {
	const aTicketDateFinish = getTicketFieldsEndDate(a);
	const bTicketDateFinish = getTicketFieldsEndDate(b);
	return new Date(aTicketDateFinish).getTime() - new Date(bTicketDateFinish).getTime();
}

export function getTicketFieldsStartDate(tf: TicketFields) {
	return tf.actualStart || tf.actualStartInferred || tf.plannedStart || null;
}

export function getTicketFieldsEndDate(tf: TicketFields) {
	return tf.actualFinish || tf.plannedFinish || tf.lastPlannedFinish || null;
}

export function sortMembersByName(a: ProjectMember, b: ProjectMember): number {
	if (anyNullOrUndefined(a.view.user, b.view.user)) {
		return nullComp(a.view.user, b.view.user);
	}
	return (a.view.user.firstName + ' ' + a.view.user.lastName).localeCompare(
		b.view.user.firstName + ' ' + b.view.user.lastName
	);
}

export function sortRolesByName(a: Role, b: Role): number {
	if (anyNullOrUndefined(a.view, b.view)) {
		return nullComp(a.view, b.view);
	}
	return a.view.roleName.localeCompare(b.view.roleName);
}

export function sortLocationsByName(a: Location, b: Location): number {
	if (anyNullOrUndefined(a.view, b.view)) {
		return nullComp(a.view, b.view);
	}
	return a.view.name.localeCompare(b.view.name);
}

export function sortInvitedByEmail(a: InvitedUser, b: InvitedUser): number {
	if (anyNullOrUndefined(a.view, b.view)) {
		return nullComp(a.view, b.view);
	}
	return a.view.email.localeCompare(b.view.email);
}

export function sortCustomField(a: CustomFieldDefinition, b: CustomFieldDefinition): number {
	if (anyNullOrUndefined(a.view, b.view)) {
		return nullComp(a.view, b.view);
	}
	return a.view.order - b.view.order;
}

export function sortCustomFieldDropdownItem(
	a: ICustomFieldDropDownItemDefinition,
	b: ICustomFieldDropDownItemDefinition
): number {
	if (anyNullOrUndefined(a.order, b.order)) {
		return nullComp(a.order, b.order);
	}
	return a.order - b.order;
}
